import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Field, PageHeader, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCcw, MapPinned, Eye, FileText, Truck, Package } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import { fmtFecha, ymdMadrid } from "@/lib/fechas";
import { fmtEnvios, fmtNumeroDeca } from "@/lib/textos";
import { decaPdfPath } from "@/lib/publicUrls";

// Iconos por defecto de Leaflet no cargan bien con webpack; los apuntamos a la CDN.
function foldDir(s) {
  return String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

function claveCalle(s) {
  const n = foldDir(s);
  const cut = n.search(/\b\d{5}\b/);
  const head = (cut >= 0 ? n.slice(0, cut) : n).replace(/[.,;]+$/g, "").replace(/,/g, " ").replace(/\s+/g, " ").trim();
  return head;
}

function canonDirecciones(addrs) {
  const best = new Map();
  for (const a of addrs) {
    const k = claveCalle(a);
    if (!k) continue;
    const prev = best.get(k);
    if (!prev || String(a).length > String(prev).length) best.set(k, a);
  }
  const out = {};
  for (const a of addrs) {
    out[a] = best.get(claveCalle(a)) || a;
  }
  return out;
}

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const originIcon = new L.DivIcon({
  html: '<div style="background:#0051C3;border:2px solid #fff;width:14px;height:14px;border-radius:50%;box-shadow:0 0 0 2px #0051C3"></div>',
  className: "", iconSize: [14, 14],
});
const destIcon = new L.DivIcon({
  html: '<div style="background:#10b981;border:2px solid #fff;width:14px;height:14px;border-radius:50%;box-shadow:0 0 0 2px #10b981"></div>',
  className: "", iconSize: [14, 14],
});

export default function MapaPage() {
  const nav = useNavigate();
  const [allItems, setAllItems] = useState([]);
  const hoy = ymdMadrid(new Date());
  const [q, setQ] = useState({ desde: hoy, hasta: hoy });
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [rutas, setRutas] = useState([]);
  const geoCache = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/v1/aggregates/mapa");
      setAllItems(data.items || []);
    } catch (_e) { setAllItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const items = useMemo(() => allItems.filter((it) => {
    const f = ymdMadrid(it.fecha);
    if (q.desde && (!f || f < q.desde)) return false;
    if (q.hasta && (!f || f > q.hasta)) return false;
    return true;
  }), [allItems, q]);

  const decasVista = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const id = it.deca_id;
      if (!id) continue;
      if (!map.has(id)) {
        map.set(id, {
          deca_id: id,
          numero_deca: it.numero_deca,
          fecha: it.fecha,
          estado: it.estado,
          version: it.version,
          cargador: it.cargador,
          transportista: it.transportista,
          matriculas: it.matriculas || [],
          conductor: it.conductor,
          envios: [],
        });
      }
      map.get(id).envios.push({
        origen: it.origen,
        destino: it.destino,
        destinatario: it.destinatario,
        bultos: it.bultos,
        peso: it.peso,
      });
    }
    return [...map.values()];
  }, [items]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (items.length === 0) { setRutas([]); setGeocoding(false); return; }
      const rawAddrs = items.flatMap((it) => [it.origen, it.destino].filter(Boolean));
      const canon = canonDirecciones(rawAddrs);
      const uniqueAddrs = [...new Set(Object.values(canon))];
      const pending = uniqueAddrs.filter((addr) => !geoCache.current[addr]);
      if (pending.length) setGeocoding(true);
      for (const addr of pending) {
        try {
          const { data } = await api.get("/api/v1/geocode", { params: { q: addr } });
          geoCache.current[addr] = data;
          if (!data.cached) await new Promise((r) => setTimeout(r, 1100));
          if (cancelled) return;
        } catch (_e) { geoCache.current[addr] = { lat: null, lon: null }; }
      }
      if (cancelled) return;
      const cache = geoCache.current;
      const out = items.map((it) => {
        const oKey = canon[it.origen] || it.origen;
        const dKey = canon[it.destino] || it.destino;
        return {
          origen: oKey, destino: dKey, deca_id: it.deca_id, destinatario: it.destinatario, bultos: it.bultos, peso: it.peso,
          oCoord: cache[oKey] && cache[oKey].lat != null ? [cache[oKey].lat, cache[oKey].lon] : null,
          dCoord: cache[dKey] && cache[dKey].lat != null ? [cache[dKey].lat, cache[dKey].lon] : null,
        };
      });
      setRutas(out.filter((r) => r.oCoord || r.dCoord));
      setGeocoding(false);
    })();
    return () => { cancelled = true; };
  }, [items]);

  const center = useMemo(() => {
    if (rutas.length === 0) return [40.4168, -3.7038]; // Madrid
    const pt = rutas[0].oCoord;
    return pt;
  }, [rutas]);

  return (
    <div>
      <PageHeader
        title="Mapa de envíos"
        subtitle="Orígenes y destinos de los DeCA para planificar la ruta óptima de un vistazo."
        testid="page-mapa"
        actions={<Button variant="outline" onClick={load} disabled={loading}><RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin":""}`}/> Actualizar</Button>}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Desde">
              <DatePicker value={q.desde} onChange={(v)=>setQ({...q, desde: v || hoy})} testid="mapa-desde"/>
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Hasta">
              <DatePicker value={q.hasta} onChange={(v)=>setQ({...q, hasta: v || hoy})} testid="mapa-hasta"/>
            </Field>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-slate-600" data-testid="mapa-legend">
          <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-blue-700"/> Origen</div>
          <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-emerald-500"/> Destino</div>
          <div className="ml-auto">
            {geocoding ? <span className="text-blue-700" data-testid="mapa-geocoding">Geocodificando direcciones…</span> : <span>{rutas.length} de {items.length} envíos geocodificados</span>}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="mapa-card">
        <div style={{ height: 560, width: "100%" }}>
          <MapContainer center={center} zoom={6} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {rutas.map((r, i) => (
              <React.Fragment key={i}>
                {r.oCoord ? (
                  <Marker position={r.oCoord} icon={originIcon}>
                    <Popup><b>Origen</b><br/>{r.origen}</Popup>
                  </Marker>
                ) : null}
                {r.dCoord ? (
                  <Marker position={r.dCoord} icon={destIcon}>
                    <Popup><b>Destino</b><br/>{r.destinatario}<br/>{r.destino}<br/><small>{r.bultos ?? "—"} bultos · {r.peso ?? 0} kg</small></Popup>
                  </Marker>
                ) : null}
                {r.oCoord && r.dCoord ? (
                  <Polyline positions={[r.oCoord, r.dCoord]} pathOptions={{ color: "#0051C3", weight: 2, opacity: 0.5, dashArray: "6 4" }}/>
                ) : null}
              </React.Fragment>
            ))}
          </MapContainer>
        </div>
      </Card>

      {decasVista.length > 0 ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="mapa-tarjetas">
          {decasVista.map((d) => (
            <Card
              key={d.deca_id}
              className="p-3 hover:border-blue-300 transition-colors"
              data-testid={`mapa-card-${d.deca_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  className="min-w-0 text-left"
                  onClick={() => nav(`/decas/${d.deca_id}`)}
                >
                  <div className="font-mono text-sm font-semibold text-slate-900 truncate">
                    {fmtNumeroDeca(d)}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {fmtFecha(d.fecha)}
                    {d.version ? ` · v${d.version}` : ""}
                    {" · "}
                    {fmtEnvios(d.envios.length)}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {d.estado ? <StatusBadge estado={d.estado} /> : null}
                  <a href={decaPdfPath(d.deca_id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" aria-label="PDF">
                      <FileText className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                  <Button size="sm" className="h-7 w-7 p-0 bg-blue-700 hover:bg-blue-800" onClick={() => nav(`/decas/${d.deca_id}`)} aria-label="Abrir">
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 space-y-1 text-xs text-slate-600">
                {d.cargador ? (
                  <div className="truncate"><span className="text-slate-400">Cargador</span> {d.cargador}</div>
                ) : null}
                {d.transportista ? (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Truck className="h-3 w-3 shrink-0 text-slate-400" />
                    <span className="truncate">{d.transportista}</span>
                  </div>
                ) : null}
                {(d.matriculas || []).length ? (
                  <div className="font-mono truncate">{(d.matriculas || []).join(" · ")}</div>
                ) : null}
                {d.conductor ? (
                  <div className="truncate"><span className="text-slate-400">Conductor</span> {d.conductor}</div>
                ) : null}
              </div>
              <div className="mt-2 space-y-1">
                {d.envios.slice(0, 2).map((e, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                    <Package className="h-3 w-3 shrink-0 text-emerald-600 mt-0.5" />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-700">{e.destinatario || e.destino || "Destino"}</div>
                      <div className="truncate text-slate-500">
                        {e.destino && e.destinatario ? e.destino : ""}
                        {e.bultos || e.peso ? `${e.destino && e.destinatario ? " · " : ""}${e.bultos ?? "—"} bultos · ${e.peso ?? 0} kg` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                {d.envios.length > 2 ? (
                  <div className="text-[11px] text-slate-400 pl-4">
                    {"+" + (d.envios.length - 2) + " m" + String.fromCharCode(0xe1) + "s"}
                  </div>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {rutas.length === 0 && !loading && !geocoding ? (
        <div className="mt-4 text-sm text-slate-500 text-center">
          <MapPinned className="h-6 w-6 mx-auto text-slate-400"/>
          <div className="mt-1">
            {items.length === 0
              ? "No hay env" + String.fromCharCode(0xed) + "os en el periodo."
              : "No se pudo situar origen o destino en el mapa. Prueba Actualizar; el mapa usa calle, CP y municipio."}
          </div>
        </div>
      ) : null}
    </div>
  );
}
